/**
 * News marker layer renderer.
 * ScatterplotLayer showing geo-located news events colored by AI threat-level
 * classification. Events without a classifier score fall back to severity.
 *
 * Recent news items (<30s old) pulse with a fading ring that matches
 * Worldmonitor's sinusoidal animation system.
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const NEWS_MARKER_LAYER_IDS = new Set(['news-markers']);

/** Duration (ms) of the pulse animation for new news markers. */
const PULSE_DURATION = 30_000;

/**
 * Threat-level color spectrum:
 * 5 (critical) → red, 4 (high) → orange, 3 (medium) → amber,
 * 2 (low) → cyan, 1 (info) → slate.
 */
/** Threat-level colors — matching WorldMonitor exactly. */
const THREAT_LEVEL_COLORS: [number, number, number][] = [
  [68, 136, 255],    // 0 — unclassified / blue
  [68, 136, 255],    // 1 — info / blue
  [136, 255, 68],    // 2 — low / green
  [255, 170, 0],     // 3 — medium / amber
  [255, 102, 0],     // 4 — high / orange
  [255, 32, 32],     // 5 — critical / red
];

/** Threat-level alpha — matching WorldMonitor. */
const THREAT_LEVEL_ALPHA: number[] = [
  80,   // 0
  80,   // 1 — info
  120,  // 2 — low
  160,  // 3 — medium
  190,  // 4 — high
  220,  // 5 — critical
];

/** Severity → threat level fallback mapping. */
const SEVERITY_TO_THREAT: Record<Severity, number> = {
  critical: 5,
  high: 4,
  medium: 3,
  low: 2,
  info: 1,
};

/** Get threat level (1–5) from event metadata or severity. */
function getThreatLevel(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;

  // AI classifier threat score (1–5)
  if (typeof raw?.threatLevel === 'number') {
    return Math.max(0, Math.min(5, Math.round(raw.threatLevel)));
  }

  // Fall back to severity mapping
  return SEVERITY_TO_THREAT[event.severity ?? 'info'];
}

/** Get news marker color from threat level. */
function getNewsColor(event: NormalizedEvent): [number, number, number] {
  const level = getThreatLevel(event);
  return THREAT_LEVEL_COLORS[level] ?? THREAT_LEVEL_COLORS[0];
}

/** Get news marker radius from threat level. */
function getNewsRadius(event: NormalizedEvent): number {
  const level = getThreatLevel(event);
  return 3 + level * 1.2;
}

/**
 * Pulsing ring for recent news (< 30s old). Time-varying at build (ring
 * existence, fade, and phase all derive from Date.now()), so the caller
 * must build it FRESH on every rebuild — never serve it from the
 * layer-instance cache, where it would freeze as a false freshness signal.
 */
export function createNewsMarkerPulseRing(
  events: NormalizedEvent[],
  zoom: number,
): Layer | null {
  const now = Date.now();
  const opacity = zoom < 2.5 ? 0.4 : zoom < 4 ? 0.7 : 1.0;

  const recentNews = events.filter((e) => {
    const age = now - new Date(e.timestamp).getTime();
    return age >= 0 && age < PULSE_DURATION;
  });
  if (recentNews.length === 0) return null;

  // Sinusoidal pulse matching WorldMonitor: amplitude 1.5, period ~318ms
  const pulse = 1.0 + 1.5 * (0.5 + 0.5 * Math.sin(now / 318));

  return new ScatterplotLayer<NormalizedEvent>({
    id: 'news-marker-pulse',
    data: recentNews,
    pickable: false,
    filled: false,
    stroked: true,
    radiusUnits: 'pixels' as const,
    radiusMinPixels: 6,
    radiusMaxPixels: 30,
    lineWidthMinPixels: 1.5,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getLineColor: (d) => {
      const color = getNewsColor(d);
      const age = now - new Date(d.timestamp).getTime();
      const fadeOut = Math.max(0, 1 - age / PULSE_DURATION);
      const level = getThreatLevel(d);
      const alpha = Math.round((THREAT_LEVEL_ALPHA[level] ?? 80) * fadeOut);
      return [...color, alpha] as [number, number, number, number];
    },
    getRadius: (d) => getNewsRadius(d) * pulse,
    opacity: opacity * 0.6,
    updateTriggers: {
      getRadius: [now],
      getLineColor: [now],
    },
  });
}

/** Create deck.gl layers for news marker data (cacheable — the pulse ring
 *  lives in createNewsMarkerPulseRing and is built per rebuild). */
export function createNewsMarkerLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  // Zoom-dependent opacity — matching WorldMonitor step thresholds
  const opacity = zoom < 2.5 ? 0.4 : zoom < 4 ? 0.7 : 1.0;
  const layers: Layer[] = [];

  // Outer glow for high-threat news
  const highThreat = events.filter((e) => getThreatLevel(e) >= 4);
  if (highThreat.length > 0) {
    layers.push(new ScatterplotLayer<NormalizedEvent>({
      id: 'news-marker-glow',
      data: highThreat,
      pickable: false,
      filled: true,
      radiusUnits: 'pixels' as const,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getFillColor: (d) => {
        const color = getNewsColor(d);
        return [...color, 40] as [number, number, number, number];
      },
      getRadius: (d) => getNewsRadius(d) + 8,
      opacity: opacity * 0.4,
    }));
  }

  // Core news dots — matching WorldMonitor
  layers.push(new ScatterplotLayer<NormalizedEvent>({
    id: 'news-marker-dots',
    data: events,
    pickable: true,
    filled: true,
    radiusUnits: 'pixels' as const,
    radiusMinPixels: 3,
    radiusMaxPixels: 12,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getFillColor: (d) => {
      const color = getNewsColor(d);
      const level = getThreatLevel(d);
      return [...color, THREAT_LEVEL_ALPHA[level] ?? 80] as [number, number, number, number];
    },
    getRadius: (d) => getNewsRadius(d),
    opacity,
    updateTriggers: {
      getFillColor: [events.length],
      getRadius: [events.length],
    },
  }));

  return layers;
}
